import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Undo2, X } from 'lucide-react';

interface UndoToastData {
  id: string;
  message: string;
  onUndo: () => void;
  duration?: number; // ms
}

type UndoToastListener = (toast: UndoToastData) => void;
let undoToastListener: UndoToastListener | null = null;

/**
 * Show a soft-delete undo toast. Returns immediately; toast auto-dismisses
 * after `duration` ms (default 10s). Clicking "Undo" calls onUndo and
 * dismisses the toast.
 */
export function showUndoToast(toast: Omit<UndoToastData, 'id'>): void {
  const id = crypto.randomUUID();
  undoToastListener?.({ ...toast, id });
}

export function UndoToastContainer() {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<UndoToastData[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Register as singleton listener
  useEffect(() => {
    undoToastListener = (toast) => {
      setToasts((prev) => [toast, ...prev].slice(0, 3));
    };
    return () => {
      undoToastListener = null;
    };
  }, []);

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Auto-dismiss timers
  useEffect(() => {
    toasts.forEach((toast) => {
      if (!timersRef.current.has(toast.id)) {
        const timer = setTimeout(
          () => dismissToast(toast.id),
          toast.duration ?? 10_000
        );
        timersRef.current.set(toast.id, timer);
      }
    });
  }, [toasts, dismissToast]);

  const handleUndo = useCallback(
    (toast: UndoToastData) => {
      toast.onUndo();
      dismissToast(toast.id);
    },
    [dismissToast]
  );

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 w-[min(420px,90vw)] pointer-events-none">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className="pointer-events-auto animate-in slide-in-from-bottom fade-in duration-300 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 shadow-2xl rounded-xl p-3 flex items-center gap-3"
          role="status"
        >
          <Trash2 className="w-5 h-5 flex-shrink-0 text-amber-400 dark:text-amber-600" />

          <p className="flex-1 text-sm font-medium truncate">{toast.message}</p>

          <button
            onClick={() => handleUndo(toast)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold text-sm transition-colors flex-shrink-0"
          >
            <Undo2 className="w-4 h-4" />
            {t('softDelete.undo')}
          </button>

          <button
            onClick={() => dismissToast(toast.id)}
            className="p-1 rounded-lg text-gray-400 hover:text-gray-200 dark:hover:text-gray-700 transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
